import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { User } from "../types";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useAddMemberMutation, useUpdateMemberMutation } from "../store/api/workspaces";
import { toast } from "sonner";
import { handleApiError } from "../utils/apiErrors";
import * as yup from "yup";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
interface EditUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
}

interface FormErrors {
  name?: string;
  email?: string;
}

export default function EditUserModal({
  open,
  onOpenChange,
  user,
}: EditUserModalProps) {
  const { t } = useLanguage();
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const [addMember, { isLoading: isCreating }] = useAddMemberMutation();
  const [updateMember, { isLoading: isUpdating }] = useUpdateMemberMutation();
  const isLoading = isCreating || isUpdating;
  const dialogContentRef = useRef<HTMLDivElement>(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });
  
  const [errors, setErrors] = useState<FormErrors>({});

  // Yup validation schema
  const getValidationSchema = (isEditing: boolean) => {
    return yup.object().shape({
      name: yup
        .string()
        .required(t('users.validation.nameRequired'))
        .max(50, t('users.validation.nameMaxLength')),
      email: isEditing
        ? yup.string() // Email not required when editing (it's disabled)
        : yup
            .string()
            .required(t('users.validation.emailRequired'))
            .email(t('users.validation.emailInvalid'))
            .max(50, t('users.validation.emailMaxLength')),
    });
  };

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
      });
    } else {
      setFormData({
        name: "",
        email: "",
      });
    }
    // Clear errors when modal opens/closes or user changes
    setErrors({});
  }, [user, open]);

  const validateForm = async (): Promise<boolean> => {
    try {
      const schema = getValidationSchema(!!user);
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const newErrors: FormErrors = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path as keyof FormErrors] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }
    
    if (!currentWorkspace) {
      toast.error(t('users.noWorkspaceSelected'));
      return;
    }
    
    if (user) {
      // Edit existing user via API
      try {
        await updateMember({
          workspaceId: currentWorkspace.id,
          userId: user.id,
          data: {
            name: formData.name,
          }
        }).unwrap();
        
        toast.success(t('users.userUpdated'));
        onOpenChange(false);
      } catch (error: any) {
        handleApiError(error, t('users.userUpdateFailed'));
      }
    } else {
      // Create new user - add to workspace with 'member' role
      try {
        await addMember({
          workspaceId: currentWorkspace.id,
          data: {
            email: formData.email,
            name: formData.name,
            role: 'member',
            sendInvitation: true,
            emailNotificationEnabled: true,
          }
        }).unwrap();
        
        toast.success(t('users.userCreated'));
        onOpenChange(false);
      } catch (error: any) {
        handleApiError(error, t('users.userCreateFailed'));
      }
    }
  };

  // Clear field error when user types
  const handleFieldChange = (field: keyof FormErrors, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  // Scroll to top when modal opens
  useEffect(() => {
    if (open && dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {user ? t('users.editUser') : t('users.createUser')}
          </DialogTitle>
          <DialogDescription>
            {t('users.userSettings')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="mb-2" htmlFor="name">{t('users.userName')} *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              className={errors.name ? "border-red-500" : ""}
              maxLength={50}
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div>
            <Label className="mb-2" htmlFor="email">{t('users.userEmail')} *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              disabled={!!user} // Email cannot be changed when editing
              maxLength={50}
              className={`${user ? "bg-gray-100" : ""} ${errors.email ? "border-red-500" : ""}`}
              required
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? t('users.processing')
                : (user ? t('common.update') : t('common.create'))}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
